import { useInfiniteQuery } from "react-query";

import type {
  GetPrizesElementResponseType,
  GetPrizesInputType,
  GetPrizesOrderByType,
  RewardFilterType,
  RewardTypeGeneral,
} from "@jti/rest-api-types";

import { QUERY_KEYS } from "../../../constants/queryKeys";
import { LoyaltyApi } from "../../../services";
import { getIsVirtualFlag } from "../../../utils/getIsVirtualFlag";

type ApiResponse = {
  items: GetPrizesElementResponseType[];
  total: number;
};

export type UseLoyaltyPrizesInputs = {
  perPage: number;
  isTeaser?: boolean;
  initialPage?: number;
  enabled?: boolean;
  consumerUuid?: string;
  categoryKey?: RewardFilterType;
  rewardType?: RewardTypeGeneral;
  orderBy?: GetPrizesOrderByType;
  sort?: "asc" | "desc";
  availableForMe?: boolean;
  minPoints?: number;
  maxPoints?: number;
  levels?: string[];
  subcategories?: string[];
};

export function useLoyaltyPrizes({
  perPage,
  consumerUuid,
  isTeaser = false,
  initialPage = 1,
  enabled = true,
  categoryKey,
  rewardType,
  orderBy: orderByOverride,
  sort: sortOverride,
  availableForMe,
  minPoints,
  maxPoints,
  levels,
  subcategories,
}: UseLoyaltyPrizesInputs) {
  const sort: GetPrizesInputType["sort"] = sortOverride ?? (isTeaser ? "desc" : "asc");
  const orderBy: GetPrizesInputType["orderBy"] = orderByOverride ?? (isTeaser ? "created_at" : "points");

  const is_virtual = getIsVirtualFlag(categoryKey);
  const is_lottery = categoryKey === "lottery" ? "1" : undefined;
  const type = categoryKey === "all" ? undefined : categoryKey;
  const available_for_me = availableForMe ? "1" : undefined;

  const query = useInfiniteQuery<ApiResponse, Error>(
    [
      QUERY_KEYS.loyaltyPrizes,
      {
        perPage,
        isTeaser,
        sort,
        orderBy,
        consumerUuid,
        categoryKey,
        available_for_me,
        minPoints,
        maxPoints,
        levels,
        subcategories,
      },
    ],
    async ({ pageParam = initialPage }) => {
      const res = await LoyaltyApi.init().getPrizes({
        page: pageParam,
        perPage,
        sort,
        orderBy,
        consumer_uuid: consumerUuid,
        is_lottery,
        is_virtual,
        type,
        rewardType,
        available_for_me,
        min_points: minPoints,
        max_points: maxPoints,
        levels,
        subcategories,
      });
      return res;
    },
    {
      refetchOnWindowFocus: false,
      enabled,
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((acc, current) => acc + current.items.length, 0);
        const total = lastPage.total ?? loaded;
        const noMoreByTotal = loaded >= total;
        const noMoreByShortPage = lastPage.items.length < perPage;
        if (noMoreByTotal || noMoreByShortPage) return undefined;
        return initialPage + allPages.length;
      },
    }
  );

  const prizes = query.data?.pages.flatMap((p) => p.items) ?? [];
  const total = query.data?.pages?.[0]?.total ?? prizes.length;

  const refetchAllPages = async () => {
    await query.refetch({
      refetchPage: () => true,
    });
  };

  return {
    prizes,
    total,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    isSuccess: query.isSuccess,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    refetchAllPages,
  };
}
